import { NgClass } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  computed,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { EMeetingDuration, ETimeFormat, dateTimeFacade, dateTimeHelper } from '@cf/core';
import { SelectNewComponent, localDataProvider } from '@cf/shared/select';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { DurationPipe } from '@cf/shared/ui/typography';
import { NgxTolgeeModule } from '@tolgee/ngx';

let id = 0;
@Component({
  selector: 'cf-duration-toggle',
  templateUrl: './duration-toggle.component.html',
  styleUrls: ['./duration-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    SelectNewComponent,
    FormsModule,
    MatButtonToggle,
    DurationPipe,
    NgClass,
    MatButtonToggleGroup,
    CfIconDirective,
    NgxTolgeeModule,
  ],
})
export class DurationToggleComponent implements OnInit, AfterViewChecked {
  currentCheckedId = `checkedButton-${id++}`;

  @ViewChild('buttonGroupWrapper')
  buttonGroup?: ElementRef<HTMLDivElement>;

  availableDurations = input.required<string[]>();

  @Input() switchToDropdown = true;

  @Input() disabled = false;

  @Input() toggleType: 'default' | 'yellow' = 'default';

  @Input() skipScroll = false;

  @Input() cutMin = false;

  @Input() set duration(value: string) {
    if (value) {
      if (dateTimeHelper.isValidFormat(`${value}`, ETimeFormat.TIME_SECOND_24)) {
        if (this.availableDurations().includes(value)) {
          this._duration = value;
        }
      } else {
        const d = dateTimeFacade.startOf('day').plus({ minute: +value }).toFormat('HH:mm:ss');

        if (this.availableDurations().includes(d)) {
          this._duration = d;
        }

        if (this.availableDurations().includes(value)) {
          this._duration = value;
        }
      }
    }
  }

  @Output() emitDurationChange = new EventEmitter();

  defaultDurations: string[] = [];

  _duration!: string;

  _leftPosition = 0;

  _scrolled = false;

  itemsForSelect = computed(() =>
    this.availableDurations().map((value) => ({
      label: value,
      value,
    })),
  );

  durationProvider = localDataProvider(this.itemsForSelect);

  constructor(
    private elementRef: ElementRef,
    private readonly cd: ChangeDetectorRef,
  ) {}

  get notFit(): boolean {
    return (
      this.buttonGroup?.nativeElement !== undefined &&
      this.buttonGroup?.nativeElement?.scrollWidth > this.elementRef.nativeElement.clientWidth
    );
  }

  get minutes() {
    return dateTimeFacade.timeToDuration(this._duration);
  }

  ngOnInit() {
    if (!this._duration) {
      if (this.availableDurations().includes(EMeetingDuration['30 mins'])) {
        this._duration = EMeetingDuration['30 mins'];
      } else {
        this._duration = this.availableDurations().find(Boolean) as string;
      }
    }
    this.defaultDurations = [...this.availableDurations()];
  }

  ngAfterViewChecked() {
    if (!this.switchToDropdown && this.buttonGroup?.nativeElement) {
      const isWrapperScrollable =
        this.buttonGroup.nativeElement.scrollWidth > this.elementRef.nativeElement.clientWidth;
      const element = this.elementRef.nativeElement.querySelector(`#${this.currentCheckedId}`);
      if (element && !this._scrolled && !this.skipScroll && isWrapperScrollable) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
            inline: 'nearest',
          });
        }, 500);

        this._scrolled = true;

        setTimeout(() => {
          if (!this.buttonGroup) return;
          this._leftPosition = this.buttonGroup.nativeElement.scrollLeft;
        }, 1500);
      }
    }

    this.cd.detectChanges();
  }

  moveTab(direction: 'left' | 'right') {
    if (!this.buttonGroup) return;
    const availableLeftPosition =
      this.buttonGroup.nativeElement.scrollWidth - this.buttonGroup.nativeElement.clientWidth;
    if (direction === 'right') {
      this._leftPosition += 200;
      if (this._leftPosition >= availableLeftPosition) {
        this._leftPosition = availableLeftPosition;
      }
    } else {
      this._leftPosition -= 200;
      if (this._leftPosition < 0) {
        this._leftPosition = 0;
      }
    }
    this.buttonGroup.nativeElement.scrollTo({
      left: this._leftPosition,
      behavior: 'smooth',
    });

    this.cd.detectChanges();
  }

  durationChange(value: string) {
    this.emitDurationChange.emit(dateTimeFacade.timeToDuration(value));
    this.cd.detectChanges();
  }
}
